import { CODES, type CodeCategory, type FuiCode } from '@fragments-sdk/core';
import {
  getRuleCatalogEntry,
  getRuleFamily,
  getRulePolicyKey,
  formatRuleLabel,
  type GovernanceRuleFamily,
} from './govern-rule-catalog';
import type { ConvexRuleKey } from './govern-rules';

export interface GovernanceCodeCatalogEntry extends FuiCode {
  family: GovernanceRuleFamily;
  familyLabel: string;
  ruleLabel: string;
  policyKey?: ConvexRuleKey;
}

export const CODE_CATEGORY_LABELS: Record<CodeCategory, string> = {
  'canonical-usage': 'Canonical usage',
  tokens: 'Tokens',
  a11y: 'Accessibility',
  states: 'States',
  composition: 'Composition',
  props: 'Props',
  performance: 'Performance',
  safety: 'Safety',
  system: 'System',
};

const FAMILY_LABELS: Record<GovernanceRuleFamily, string> = {
  styles: 'Style',
  tokens: 'Token',
  components: 'Component',
  a11y: 'Accessibility',
  safety: 'Safety',
  brand: 'Brand',
  tailwind: 'Tailwind',
  other: 'Governance',
};

export const GOVERNANCE_CODE_CATALOG: readonly GovernanceCodeCatalogEntry[] =
  CODES.map((entry) => {
    const catalog = getRuleCatalogEntry(entry.ruleId);
    const family = catalog?.family ?? getRuleFamily(entry.ruleId);
    return {
      ...entry,
      family,
      familyLabel: FAMILY_LABELS[family],
      policyKey: catalog?.policyKey ?? getRulePolicyKey(entry.ruleId),
      ruleLabel: catalog?.label ?? formatRuleLabel(entry.ruleId),
    };
  });

const CODE_BY_CODE = new Map(
  GOVERNANCE_CODE_CATALOG.map((entry) => [entry.code, entry]),
);
const CODE_BY_RULE_ID = new Map(
  GOVERNANCE_CODE_CATALOG.map((entry) => [entry.ruleId, entry]),
);

export function getGovernanceCodeEntry(input: {
  code?: string;
  ruleId?: string;
}): GovernanceCodeCatalogEntry | undefined {
  if (input.code) {
    const exact = CODE_BY_CODE.get(input.code);
    if (exact) return exact;
  }
  return input.ruleId ? CODE_BY_RULE_ID.get(input.ruleId) : undefined;
}

export function formatGovernanceCodeLabel(input: {
  code?: string;
  ruleId?: string;
}): string {
  return getGovernanceCodeEntry(input)?.code ?? input.code ?? 'Uncoded';
}

export function formatGovernanceCodeTitle(input: {
  code?: string;
  ruleId?: string;
}): string {
  const entry = getGovernanceCodeEntry(input);
  return entry?.title ?? input.ruleId ?? 'No FUI code mapped';
}
